<template>
  <form @submit.prevent="onSubmit" class="form">
    <div class="form__post">
      <label class="post-input">
        <p class="post-input__title">記事ID</p>
        <input
          type="text"
          name="post-id"
          v-model="editingPostId"
          class="post-input__input"
        />
      </label>
      <div class="post-input__post-preview post-preview">
        <p class="post-preview__title">該当記事</p>
        <div class="post-preview__link">
          <a v-if="displayingPost" :href="editingPostUrl" target="_blank">{{
            displayingPost.title
          }}</a>
          <span v-else>該当無し</span>
        </div>
      </div>
    </div>
    <div class="form__tag-conditions tag-conditions">
      <p class="tag-conditions__title">表示条件</p>
      <ul class="tag-conditions__body conditions">
        <li
          v-for="(editingTagCondition, index) in editingTagConditions"
          :key="`tag-cond-${index}`"
          class="conditions__item condition-item"
        >
          <p v-if="index > 0" class="condition-item__divider">かつ</p>
          <AdPostEditorTagList
            :index="index"
            :tags="editingTagCondition"
            class="condition-item__tag-list"
            @edit-tags="onEditTags"
            @delete-tags="onDeleteTags"
          />
        </li>
      </ul>
      <button type="button" class="tag-conditions__button" @click="onAddTags">
        <i class="fa fa-plus"></i> 表示条件を追加
      </button>
    </div>
    <footer class="form__footer footer">
      <button
        type="button"
        :disabled="!isDeleteButtonEnabled"
        class="footer__delete"
        @click="onDelete"
      >
        <i class="fa fa-trash"></i>
        <span v-if="seriouslyDelete">ほんま？</span>
      </button>
      <button
        type="submit"
        :disabled="!isSubmitButtonEnabled"
        class="footer__submit"
      >
        {{ submitButtonLabel }}
      </button>
    </footer>
  </form>
</template>

<script>
import debounce from 'debounce';
import deepEqual from 'deep-equal';
import AdPostEditorTagList from './AdPostEditorTagList.vue';
import { restClient } from '../../../libs/restClient';

export default {
  name: 'AdPostEditor',
  components: {
    AdPostEditorTagList,
  },
  props: {
    initialAdPost: {
      type: Object,
      default: undefined,
    },
    position: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      editingPostId: this.initialAdPost && this.initialAdPost.post.id,
      displayingPost: this.initialAdPost && this.initialAdPost.post,
      editingTagConditions:
        (this.initialAdPost && this.initialAdPost.tags) || [],
      seriouslyDelete: false,
    };
  },
  computed: {
    editingPostUrl() {
      return `/admins/posts/${this.editingPostId}/edit`;
    },
    isDeleteButtonEnabled() {
      return this.initialAdPost !== undefined;
    },
    isSubmitButtonEnabled() {
      const initialAdPostIdStructure = {
        postId: this.initialAdPost && this.initialAdPost.post.id,
        tags:
          this.initialAdPost && this.initialAdPost.tags.length > 0
            ? this.initialAdPost.tags.map((tagCondition) =>
                tagCondition.map((tag) => tag.id)
              )
            : [],
      };

      const editingAdPostIdStructure = {
        postId: this.editingPostId,
        tags:
          this.editingTagConditions.length > 0
            ? this.editingTagConditions.map((editingTagCondition) =>
                editingTagCondition.map((tag) => tag.id)
              )
            : [],
      };

      return (
        this.displayingPost !== undefined &&
        this.editingTagConditions.length > 0 &&
        !deepEqual(initialAdPostIdStructure, editingAdPostIdStructure)
      );
    },
    debouncedFetchPost() {
      const dispatcher = (...args) => {
        this.fetchPost(...args);
      };
      return debounce(dispatcher, 800);
    },
    submitButtonLabel() {
      return this.initialAdPost === undefined ? '新規作成' : '保存';
    },
  },
  watch: {
    editingPostId(newPostId) {
      this.debouncedFetchPost(newPostId);
    },
  },
  methods: {
    async onDelete() {
      if (!this.seriouslyDelete) {
        // 確認のモーダルとかを用意するのがダルいので、2回クリックしてはじめて削除するようにする
        this.seriouslyDelete = true;
        setTimeout(() => {
          this.seriouslyDelete = false;
        }, 1000);
        return;
      }
      try {
        this.$root.$refs.loadModal.loading = true;

        await restClient.delete(
          `/admins/api/ad_posts/${this.initialAdPost.id}`
        );
        this.$emit('delete-ad-post', { id: this.initialAdPost.id });
        this.$root.$refs.loadModal.success = true;
      } catch (error) {
        console.error(error);
        this.$root.$refs.loadModal.error = true;
      } finally {
        setTimeout(() => {
          this.$root.$refs.loadModal.loading = false;
          this.$root.$refs.loadModal.success = false;
          this.$root.$refs.loadModal.error = false;
        }, 1000);
      }
    },
    async onSubmit() {
      const payload = {
        post_id: this.editingPostId,
        tags: this.editingTagConditions.map((editingTagCondition) =>
          editingTagCondition.map((tag) => tag.id)
        ),
        position: this.position,
      };

      if (this.initialAdPost === undefined) {
        // 新規作成
        try {
          this.$root.$refs.loadModal.loading = true;
          const { data: adPost } = await restClient.post(
            '/admins/api/ad_posts',
            payload
          );
          this.$emit('update-ad-post', { adPost, isCreate: true });
          this.$root.$refs.loadModal.success = true;
        } catch (error) {
          console.error(error);
          this.$root.$refs.loadModal.error = true;
        } finally {
          setTimeout(() => {
            this.$root.$refs.loadModal.loading = false;
            this.$root.$refs.loadModal.success = false;
            this.$root.$refs.loadModal.error = false;
          }, 1000);
        }
      } else {
        // 上書き保存
        try {
          this.$root.$refs.loadModal.loading = true;
          const { data: adPost } = await restClient.put(
            `/admins/api/ad_posts/${this.initialAdPost.id}`,
            payload
          );
          this.$emit('update-ad-post', { adPost, isCreate: false });
          this.$root.$refs.loadModal.success = true;
        } catch (error) {
          console.error(error);
          this.$root.$refs.loadModal.error = true;
        } finally {
          setTimeout(() => {
            this.$root.$refs.loadModal.loading = false;
            this.$root.$refs.loadModal.success = false;
            this.$root.$refs.loadModal.error = false;
          }, 1000);
        }
      }
    },
    onAddTags() {
      this.editingTagConditions = [...this.editingTagConditions, []];
    },
    onEditTags({ index, tags }) {
      if (tags.length > 0) {
        const edited = [...this.editingTagConditions];
        edited[index] = tags;
        this.editingTagConditions = edited;
      } else {
        // 空だったら消す
        this.onDeleteTags({ index });
      }
    },
    onDeleteTags({ index }) {
      this.editingTagConditions = this.editingTagConditions.filter(
        (_, i) => i !== index
      );
    },
    async fetchPost(postId) {
      this.displayingPost = undefined;
      try {
        const { data: post } = await restClient.get(
          `/admins/api/posts/${postId}`
        );

        this.displayingPost = post;
      } catch (error) {
        this.displayingPost = undefined;
      }
    },
    startEditTags() {
      this.isTagFinderShown = true;
    },
  },
};
</script>

<style lang="scss" scoped>
@mixin title {
  font-size: 18px;
  color: #555;
}

@mixin box {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 12px 14px 14px;
}

.form {
  @include box;

  &__post {
    @include box;
  }

  &__tag-conditions {
    &:nth-child(n + 2) {
      margin-top: 20px;
    }
  }

  &__footer {
    &:nth-child(n + 2) {
      margin-top: 16px;
    }
  }
}

.post-input {
  &__title {
    @include title;
  }

  &__input {
    font-size: 18px;
    width: 100%;

    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }

  &__post-preview {
    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }
}

.post-preview {
  padding: 8px 12px;
  background-color: #eee;

  &__title {
    @include title;

    font-size: 14px;
  }

  &__link {
    font-size: 14px;

    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }
}

.tag-conditions {
  @include box;

  display: flex;
  flex-direction: column;

  &__title {
    @include title;
  }

  &__body {
    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }

  &__button {
    align-self: center;
    display: flex;
    align-items: center;
    border: 1px solid #555;
    border-radius: 2px;
    color: #555;
    background-color: #fff;

    &:hover {
      cursor: pointer;
      background-color: #eee;
    }

    &:nth-child(n + 2) {
      margin-top: 12px;
    }

    i {
      font-size: 16px;
      margin-right: 6px;
    }
  }
}

.conditions {
  &__item {
    &:nth-child(n + 2) {
      margin-top: 12px;
    }
  }
}

.condition-item {
  &__divider {
    text-align: center;
    font-size: 12px;
  }

  &__tag-list {
    &:nth-child(n + 2) {
      margin-top: 12px;
    }
  }
}

.footer {
  display: flex;
  justify-content: space-between;

  @mixin action {
    display: flex;
    align-items: center;
    border: 0;
    border-radius: 4px;
    padding: 0.4em 0.6em 0.3em;
    color: #fff;
    font-size: 18px;
    line-height: 1;

    &:hover {
      cursor: pointer;
    }

    &[disabled] {
      background-color: #eee;
      cursor: not-allowed;
    }

    span {
      font-size: 12px;

      &:nth-child(n + 2) {
        margin-left: 8px;
      }
    }
  }

  &__delete {
    @include action;

    flex: 0 0 auto;
    background-color: #f4695e;
  }

  &__submit {
    @include action;

    flex: 0 0 auto;
    background-color: #48c78e;
  }
}
</style>
